import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import contentService from '../../services/contentService';
import RichTextEditor from '../../components/common/RichTextEditor';
import ContactPageEditor from './ContactPageEditor';
import './ContentPageEditor.css';

function ContentPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [pageType, setPageType] = useState('general'); // 'general', 'contact', 'product-listing'
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    status: 'DRAFT',
    content: '',
    metaTitle: '',
    metaDescription: '',
  });

  useEffect(() => {
    if (isEditing) {
      const loadPage = async () => {
        try {
          const response = await contentService.getPage(id);
          // Handle both wrapped and unwrapped response formats
          const pageData = response.data?.data || response.data;
          setFormData(pageData);

          // Detect page type from slug and content
          if (pageData.slug === 'hall-of-fame') {
            // Hall of Fame uses plain text per API content format spec
            setPageType('product-listing');
          } else if (pageData.content) {
            try {
              const parsed = JSON.parse(pageData.content);
              // If content has locations array, it's a contact page
              if (parsed.locations && Array.isArray(parsed.locations)) {
                setPageType('contact');
              } else {
                setPageType('general');
              }
            } catch {
              // Not JSON, so it's HTML (general)
              setPageType('general');
            }
          }
        } catch (err) {
          toast.error('Failed to load page');
          navigate('/content/pages');
        } finally {
          setIsLoading(false);
        }
      };

      loadPage();
    }
  }, [id, isEditing, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-generate slug from title if creating new page
      if (field === 'title' && !isEditing && !prev.slugManuallyEdited) {
        updated.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }

      return updated;
    });
  };

  const handleSlugChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      slug: value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
      slugManuallyEdited: true,
    }));
  };

  const handleSubmit = async (status = null) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!formData.slug.trim()) {
      toast.error('Slug is required');
      return;
    }

    setIsSaving(true);

    try {
      const dataToSave = {
        ...formData,
        status: status || formData.status,
      };

      if (isEditing) {
        // Remove immutable fields from update payload
        // Backend ignores these anyway, but cleaner to not send them
        const { slug, id, authorId, createdAt, slugManuallyEdited, ...updatePayload } = dataToSave;
        await contentService.updatePage(id, updatePayload);
        toast.success('Page updated successfully');
      } else {
        const response = await contentService.createPage(dataToSave);
        toast.success('Page created successfully');
        // Handle both wrapped and unwrapped response formats
        const newPage = response.data?.data || response.data;
        navigate(`/content/pages/${newPage.id}/edit`);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save page';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = () => {
    handleSubmit('PUBLISHED');
  };

  const handleSaveDraft = () => {
    handleSubmit('DRAFT');
  };

  if (isLoading) {
    return (
      <div className="content-editor">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading page...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="editor-header-left">
          <Link to="/content/pages" className="back-link">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <div className="editor-title">
            <h1>{isEditing ? 'Edit Page' : 'New Page'}</h1>
            {formData.status && (
              <span className={`status-badge ${formData.status.toLowerCase()}`}>
                {formData.status === 'PUBLISHED' ? 'Published' : 'Draft'}
              </span>
            )}
          </div>
        </div>
        <div className="editor-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            Save Draft
          </button>
          <button
            className="btn btn-primary"
            onClick={handlePublish}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : formData.status === 'PUBLISHED' ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Editor Tabs */}
      <div className="editor-tabs">
        <button
          className={`editor-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          Content
        </button>
        <button
          className={`editor-tab ${activeTab === 'seo' ? 'active' : ''}`}
          onClick={() => setActiveTab('seo')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          SEO
        </button>
      </div>

      {/* Editor Content */}
      <div className="editor-content">
        {activeTab === 'content' && (
          <div className="editor-panel">
            <div className="form-group">
              <label className="form-label">Page Title</label>
              <input
                type="text"
                className="form-input title-input"
                placeholder="Enter page title..."
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                URL Slug
                {isEditing && (
                  <span className="lock-indicator" title="Locked to preserve URLs and SEO">
                    🔒 Locked
                  </span>
                )}
              </label>
              <div className="slug-input-group">
                <span className="slug-prefix">/</span>
                <input
                  type="text"
                  className={`form-input slug-input ${isEditing ? 'slug-locked' : ''}`}
                  placeholder={isEditing ? 'Slug cannot be changed' : 'page-url-slug'}
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  disabled={isEditing}
                  readOnly={isEditing}
                />
              </div>
              <span className="form-hint">
                {isEditing ? (
                  <>
                    ℹ️ The slug is locked to preserve existing URLs and SEO rankings. Only the title can be updated.
                  </>
                ) : (
                  <>
                    The URL path for this page (e.g., /about-us). Auto-generated from title.
                  </>
                )}
              </span>
            </div>

            {/* Content Field - Conditional Rendering based on Page Type */}
            <div className="form-group">
              <label className="form-label">Content</label>

              {pageType === 'general' && (
                <>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => handleInputChange('content', content)}
                    placeholder="Write your page content here..."
                    minHeight={400}
                  />
                  <span className="form-hint">Use the toolbar above to format your content with headings, lists, links, and more.</span>
                </>
              )}

              {pageType === 'contact' && (
                <>
                  <ContactPageEditor
                    value={formData.content}
                    onChange={(content) => handleInputChange('content', content)}
                  />
                  <span className="form-hint">Add contact locations with address, phone, and email details.</span>
                </>
              )}

              {pageType === 'product-listing' && (
                <>
                  <textarea
                    className="form-textarea"
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Enter product listing description..."
                    rows={8}
                  />
                  <span className="form-hint">Plain text description for the product listing section.</span>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="editor-panel">
            <div className="seo-preview">
              <h3>Search Engine Preview</h3>
              <div className="seo-preview-box">
                <div className="seo-preview-title">
                  {formData.metaTitle || formData.title || 'Page Title'}
                </div>
                <div className="seo-preview-url">
                  touchlinememories.com/{formData.slug || 'page-url'}
                </div>
                <div className="seo-preview-description">
                  {formData.metaDescription || 'Add a meta description to improve SEO and click-through rates from search results.'}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Meta Title</label>
              <input
                type="text"
                className="form-input"
                placeholder="SEO title (defaults to page title)"
                value={formData.metaTitle || ''}
                onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                maxLength={60}
              />
              <span className="form-hint">
                {(formData.metaTitle || '').length}/60 characters
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Meta Description</label>
              <textarea
                className="form-textarea"
                placeholder="Brief description for search engines..."
                value={formData.metaDescription || ''}
                onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                rows={3}
                maxLength={160}
              />
              <span className="form-hint">
                {(formData.metaDescription || '').length}/160 characters
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentPageEditor;
